import type {EnvironmentContext} from '@wix/document-manager-core'
import type {RecommendParams, RecommendationResult, Recommendation} from './types'
import {
    aiGatewayUrl,
    proactiveAiRecommendationPromptId,
    proactiveAiRecommendErrorType,
    proactiveAiRecommendErrorMessage,
    proactiveAiRecommendBadJsonErrorType,
    proactiveAiRecommendBadJsonErrorMessage,
    promptHubAppId
} from '../constants'
import {ReportableError} from '@wix/document-manager-utils'

const DEFAULT_SCORE_THRESHOLD = 0.3

const parseRecommendationResponse = (responseText: string, scoreThreshold: number): Recommendation[] => {
    const jsonMatch = responseText.match(/\{[\s\S]*}/)
    if (!jsonMatch) {
        throw new ReportableError({
            errorType: proactiveAiRecommendBadJsonErrorType,
            message: proactiveAiRecommendBadJsonErrorMessage,
            extras: {responseText}
        })
    }

    const parsed = JSON.parse(jsonMatch[0])

    const recommendations: Recommendation[] = parsed.recommendations.map((rec: any) => {
        const {impact, confidence, readiness} = rec.scores
        const finalScore = impact * confidence * readiness

        return {
            id: rec.id,
            componentPointer: rec.componentPointer,
            message: rec.message,
            description: rec.description,
            action: {
                type: rec.action.type,
                data: rec.action.data,
                innerElementPath: rec.action.innerElementPath
            },
            scores: {impact, confidence, readiness, final: finalScore},
            insightId: rec.insightId,
            insightType: rec.insightType
        }
    })

    return recommendations
        .filter(rec => rec.scores.final >= scoreThreshold)
        .sort((a, b) => b.scores.final - a.scores.final)
}

export const recommend = async (
    environmentContext: EnvironmentContext,
    params: RecommendParams
): Promise<RecommendationResult> => {
    const {serverFacade} = environmentContext
    const {analysisResult, maxRecommendations, scoreThreshold = DEFAULT_SCORE_THRESHOLD} = params

    if (analysisResult.insights.length === 0) {
        return {recommendations: [], limit: maxRecommendations, appliedThreshold: scoreThreshold}
    }

    const requestData = {
        appDefId: promptHubAppId,
        promptId: proactiveAiRecommendationPromptId,
        params: {
            analysis_result: JSON.stringify(analysisResult),
            max_recommendations: String(maxRecommendations ?? ''),
            score_threshold: String(scoreThreshold)
        },
        userRequestInfo: {
            additionalTags: 'proactiveAiRecommendation'
        }
    }

    try {
        const response = await serverFacade.post(aiGatewayUrl, requestData)
        if (!response.response.generatedTexts || response.response.generatedTexts.length === 0) {
            throw new ReportableError({
                errorType: proactiveAiRecommendBadJsonErrorType,
                message: 'AI response missing generated texts',
                extras: {insightCount: analysisResult.insights.length}
            })
        }
        const generatedText = response.response.generatedTexts[0]
        const recommendations = parseRecommendationResponse(generatedText, scoreThreshold)
        const limitedRecommendations = maxRecommendations
            ? recommendations.slice(0, maxRecommendations)
            : recommendations

        return {recommendations: limitedRecommendations, limit: maxRecommendations, appliedThreshold: scoreThreshold}
    } catch (e: any) {
        if (e.errorType === proactiveAiRecommendBadJsonErrorType) {
            throw e
        }
        throw new ReportableError({
            errorType: proactiveAiRecommendErrorType,
            message: proactiveAiRecommendErrorMessage,
            extras: {insightCount: analysisResult.insights.length, originalError: e?.message}
        })
    }
}
